import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Building2, Calculator, FileCheck2, Globe2, MailCheck, Stamp, Users } from "lucide-react";
import { scrollToSection } from "@/lib/content";
import { useT } from "@/i18n";

const ICONS = [Building2, MailCheck, Calculator, Users, FileCheck2, Globe2, Stamp];
const TILES = ["bg-brand-teal/10 dark:bg-brand-teal/25 text-brand-teal dark:text-teal-300", "bg-brand-red/10 dark:bg-brand-red/25 text-brand-red dark:text-red-300", "bg-brand-yellow/15 dark:bg-brand-yellow/25 text-brand-yellow dark:text-yellow-300", "bg-brand-navy/10 dark:bg-white/15 text-brand-navy dark:text-white", "bg-brand-teal/10 dark:bg-brand-teal/25 text-brand-teal dark:text-teal-300", "bg-brand-red/10 dark:bg-brand-red/25 text-brand-red dark:text-red-300", "bg-brand-yellow/15 dark:bg-brand-yellow/25 text-brand-yellow dark:text-yellow-300"];
const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-6", "md:col-span-6"];
const HREFS = [null, null, null, null, null, null, "/biuro-wirtualne-warszawa/legalizacja-dokumentow"];

export default function Services() {
  const { t } = useT();
  const navigate = useNavigate();
  const SERVICES = t.services.items.map((s, i) => ({ ...s, icon: ICONS[i], span: SPANS[i], tile: TILES[i], href: HREFS[i] }));
  return (
    <section id="biuro-wirtualne-oferta" data-testid="services-section" className="py-16 sm:py-24 lg:py-32 bg-white dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.25em] text-slate-600 dark:text-slate-400 font-semibold"
            >
              <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-red mr-3 align-middle" />{t.services.overline}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-4"
            >
              {t.services.heading}
            </motion.h2>
          </div>
          <motion.button
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="services-cta"
            onClick={() => scrollToSection("#biuro-wirtualne-kontakt")}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-tealDark transition-colors"
          >
            {t.services.cta}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </motion.button>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`service-card-${i + 1}`}
              onClick={s.href ? () => navigate(s.href) : undefined}
              role={s.href ? "link" : undefined}
              tabIndex={s.href ? 0 : undefined}
              onKeyDown={s.href ? (e) => { if (e.key === "Enter" || e.key === " ") navigate(s.href); } : undefined}
              className={`${s.span} group bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-500/30 ${s.href ? "cursor-pointer" : ""}`}
            >
              <div className={`w-11 h-11 rounded-xl ${s.tile} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
                <s.icon size={20} />
              </div>
              <h3 className="font-display font-bold text-lg sm:text-xl tracking-tight mt-5">{s.title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-2 max-w-lg">{s.text}</p>
              {s.href && (
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-teal">
                  {t.services.more} <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
